import React, { useState, useEffect } from 'react';
import { X, Search, Plus, Edit, Trash2 } from 'lucide-react';
import { GhostButton } from '../ui/GhostButton';
import { Input } from '../ui/Input';
import { AddressFormModal } from './AddressFormModal';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { ConfirmationModal } from '../ui/ConfirmationModal';

export const AddressManagementModal = ({ isOpen, onClose }) => {
    const [addresses, setAddresses] = useState([]);
    const [search, setSearch] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingAddress, setEditingAddress] = useState(null);
    const { items: sortedAddresses, requestSort, sortConfig } = useSortableData(addresses);

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null,
        isDestructive: false
    });

    const fetchAddresses = async () => {
        try {
            const queryParams = new URLSearchParams();
            if (search) queryParams.append('search', search);

            const res = await fetch(`/api/master/addresses?${queryParams.toString()}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.ok) {
                const data = await res.json();
                setAddresses(data);
            }
        } catch (err) {
            console.error("Error fetching master addresses:", err);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchAddresses();
        }
    }, [isOpen, search]);

    const handleSaveForm = async (formData) => {
        try {
            const method = editingAddress ? 'PUT' : 'POST';
            const url = editingAddress
                ? `/api/master/addresses/${editingAddress.id}`
                : '/api/master/addresses';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setIsFormOpen(false);
                setEditingAddress(null);
                fetchAddresses();
            }
        } catch (err) {
            console.error("Error saving master address:", err);
        }
    };

    const handleDeleteMaster = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Contact',
            message: 'Are you sure you want to delete this contact? It will be removed from all products.',
            isDestructive: true,
            onConfirm: async () => {
                try {
                    const res = await fetch(`/api/master/addresses/${id}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    if (res.ok) fetchAddresses();
                } catch (err) {
                    console.error("Error deleting master address:", err);
                }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-stone-900 border border-stone-800 rounded-xl shadow-2xl w-[900px] max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-6 border-b border-stone-800">
                    <h2 className="text-xl font-bold text-white">Manage Master Addresses</h2>
                    <button onClick={onClose} className="text-stone-400 hover:text-white transition">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4 border-b border-stone-800 flex gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-500" size={16} />
                        <Input
                            placeholder="Search contacts..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10 bg-stone-950 border-stone-700 w-full"
                        />
                    </div>
                    <GhostButton onClick={() => { setEditingAddress(null); setIsFormOpen(true); }}>
                        <Plus size={16} /> Create New Contact
                    </GhostButton>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-stone-950 text-stone-500 text-xs font-bold uppercase sticky top-0">
                            <tr>
                                <SortableHeader label="Company" sortKey="company_name" currentSort={sortConfig} onSort={requestSort} className="p-3" />
                                <SortableHeader label="Contact" sortKey="first_name" currentSort={sortConfig} onSort={requestSort} className="p-3" />
                                <SortableHeader label="City/Country" sortKey="city" currentSort={sortConfig} onSort={requestSort} className="p-3" />
                                <SortableHeader label="Type" sortKey="type" currentSort={sortConfig} onSort={requestSort} className="p-3" />
                                <th className="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-800 text-sm text-stone-300">
                            {sortedAddresses.map(addr => (
                                <tr key={addr.id} className="hover:bg-stone-800/50 transition-colors group">
                                    <td className="p-3 font-medium text-white">{addr.company_name}</td>
                                    <td className="p-3">{addr.first_name} {addr.last_name}</td>
                                    <td className="p-3">{addr.city}, {addr.country}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded text-xs font-medium border ${addr.type === 'Main' ? 'bg-purple-900/20 border-purple-800 text-white' :
                                            addr.type === 'Supplier' ? 'bg-blue-900/20 border-blue-800 text-white' :
                                                'bg-stone-800 border-stone-700 text-white'
                                            }`}>
                                            {addr.type}
                                        </span>
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => { setEditingAddress(addr); setIsFormOpen(true); }}
                                                className="p-1.5 rounded hover:bg-stone-700 text-stone-400 hover:text-white transition"
                                            >
                                                <Edit size={14} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteMaster(addr.id)}
                                                className="p-1.5 rounded hover:bg-red-900/30 text-stone-400 hover:text-red-500 transition"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddressFormModal
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                address={editingAddress}
                onSave={handleSaveForm}
            />

            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                isDestructive={confirmModal.isDestructive}
            />
        </div>
    );
};
